import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useRef } from "react";

import {
  DashboardRequestError,
  dashboardRequest,
  isDashboardDataProviderEnabled,
} from "../providers/aws/client";
import {
  buildLlmConfigView,
  type LlmConfigView,
  type LlmModelsResponse,
} from "./llmConfigModel";

/** How many of the newest Echoes, over how many hours, the observed half reads. */
export const LLM_MODELS_SESSIONS = 25;
export const LLM_MODELS_HOURS = 72;

export type LlmModelsLoader = (options: {
  /** Ask the API to skip its 60 s cache. */
  refresh: boolean;
}) => Promise<LlmModelsResponse>;

export function llmModelsPath(refresh: boolean): string {
  return `/config/llm-models?sessions=${LLM_MODELS_SESSIONS}&hours=${LLM_MODELS_HOURS}${
    refresh ? "&refresh=1" : ""
  }`;
}

/**
 * `GET /config/llm-models` on the dashboard API (infra dashboard-api): every
 * model assignment in the signed runtime config, the override rules, and what
 * the newest Echoes ran according to their cost-ledger rows.
 */
export const fetchLlmModels: LlmModelsLoader = ({ refresh }) =>
  dashboardRequest<LlmModelsResponse>(llmModelsPath(refresh));

/** The real loader where the dashboard API is the data provider, else none. */
export function defaultLlmModelsLoader(): LlmModelsLoader | null {
  return isDashboardDataProviderEnabled() ? fetchLlmModels : null;
}

export type LlmConfigState =
  | { status: "unavailable" }
  | { status: "loading" }
  | { status: "missing_endpoint" }
  | { status: "error"; message: string }
  | {
      status: "ready";
      view: LlmConfigView;
      /** The newest refetch failed; `view` is the last good answer. */
      refreshError: string | null;
    };

export const LLM_MODELS_REFETCH_MS = 60 * 1000;

/**
 * Live payload: its query key prefix is listed in root/persistQueryFilter.ts so
 * it is never written to localStorage and served stale in a later session.
 */
export const LLM_MODELS_QUERY_PREFIX = "llm-config";

const isMissingEndpoint = (error: unknown) =>
  error instanceof DashboardRequestError && error.status === 404;

export function useLlmModels(loader: LlmModelsLoader | null): {
  state: LlmConfigState;
  refresh: () => void;
  refreshing: boolean;
} {
  const force = useRef(false);
  const query = useQuery({
    queryKey: [
      LLM_MODELS_QUERY_PREFIX,
      "models",
      LLM_MODELS_SESSIONS,
      LLM_MODELS_HOURS,
    ],
    queryFn: () => {
      const refresh = force.current;
      force.current = false;
      return loader!({ refresh });
    },
    enabled: loader != null,
    // The page is a live readout: refetch every minute while it is open.
    refetchInterval: LLM_MODELS_REFETCH_MS,
    staleTime: LLM_MODELS_REFETCH_MS,
    // A 404 means the API predates the endpoint; retrying will not help.
    retry: (count, error) => !isMissingEndpoint(error) && count < 1,
  });
  const view = useMemo(
    () => (query.data ? buildLlmConfigView(query.data) : null),
    [query.data],
  );
  const { refetch } = query;
  const refresh = useCallback(() => {
    force.current = true;
    void refetch();
  }, [refetch]);

  let state: LlmConfigState;
  if (loader == null) state = { status: "unavailable" };
  else if (view)
    state = {
      status: "ready",
      view,
      refreshError: query.error ? String(query.error.message) : null,
    };
  else if (query.error)
    state = isMissingEndpoint(query.error)
      ? { status: "missing_endpoint" }
      : { status: "error", message: String(query.error.message) };
  else state = { status: "loading" };

  return { state, refresh, refreshing: query.isFetching };
}
